import { Grid, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { contactUsPressed } from "../../utils/contactUsPressed";
import { Icon, Logo, Link, Button, Divider } from "capitalrx-components-2.0/lib/general";
import { ReactComponent as LinkedIn } from "@/static/images/linkedin-logo.svg";
import { ReactComponent as Twitter } from "@/static/images/twitter-logo.svg";
import { ReactComponent as YouTube } from "@/static/images/youtube-logo.svg";
import { ReactComponent as Instagram } from "@/static/images/instagram.svg";
import { ReactComponent as Facebook } from "@/static/images/facebook.svg";

import DownloadAppLogo from "@/components/DownloadApp/DownloadAppLogo";

import styles from "./Footer.module.scss";
import {
  getBrandName,
  getCardBrandName,
  getCardName,
  getLogoComponentName,
} from "@/utils/branding";

const Footer = (): JSX.Element => {
  const brandName = getBrandName();
  const cardName = getCardName();
  const cardBrandName = getCardBrandName();
  const logoName = getLogoComponentName();

  const { columns31212, columns21212, columns51212 } = useResponsive({
    columns31212: {
      desktop: 3,
      tablet: 12,
      mobile: 12,
    },
    columns21212: {
      desktop: 2,
      tablet: 12,
      mobile: 12,
    },
    columns51212: {
      desktop: 5,
      tablet: 12,
      mobile: 12,
    },
  });
  return (
    <footer className={styles.FooterWrapper} data-testid="advantage-footer">
      <Divider className={styles.SectionDividerLine} aria-hidden="true" />
      <Grid className={styles.TopFooterWrapper}>
        <Grid.Spread columns={columns31212}>
          <div className={styles.FooterCapitalRXSectionWithLogo}>
            <Link
              to="https://www.judi.health/"
              className={styles.LinkToCorporate}
              testId="footer-link-capital-rx"
            >
              <span aria-hidden="true">
                <Logo
                  name={logoName}
                  testId="capital-rx-logo-footer"
                  className={styles.CapitalrxLogo}
                />
              </span>
              <span className="companyName">{brandName}</span>
            </Link>
            <div
              className={styles.FooterCapitalRXSectionWithLogoAddress}
              data-testid="advantage-footer-address"
            >
              {brandName} Headquarters
              <br />
              One World Trade Center
              <br />
              New York, NY 10007
              <br />
            </div>
            <div className={styles.FooterCapitalRXSectionWithLogoColumnContent}>
              <Link
                to="tel:(888) 617-6521"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-customer-support-phone"
              >
                <Icon name="phone" aria-hidden="true" testId="phone-icon" />
                (888) 617-6521
              </Link>
            </div>
            <div className={styles.FooterCapitalRXSectionWithLogoColumnContent}>
              <Link
                to="mailto:info@cap-rx.com"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-capitalrx-email"
              >
                <Icon name="envelope" aria-hidden="true" testId="envelope-icon" />
                info@cap-rx.com
              </Link>
            </div>
          </div>
        </Grid.Spread>
        <Grid.Spread columns={columns21212}>
          <div className={styles.FooterCapitalRXSectionFollowUs}>
            <div className={styles.FooterCapitalRXSectionFollowUsColumnTitle}>Follow Us</div>
            <div className={styles.FooterCapitalRXSectionFollowUsColumnContent}>
              <Link
                to="https://www.linkedin.com/company/cap-rx/"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-linkedin"
              >
                <LinkedIn aria-hidden="true" />
                LinkedIn
              </Link>
            </div>
            <div className={styles.FooterCapitalRXSectionFollowUsColumnContent}>
              <Link
                to="https://twitter.com/cap_rx"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-twitter"
              >
                <Twitter aria-hidden="true" />
                Twitter
              </Link>
            </div>
            <div className={styles.FooterCapitalRXSectionFollowUsColumnContent}>
              <Link
                to="https://www.youtube.com/channel/UCVAnrQMs7hzWmgwUox2vqCA"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-youtube"
              >
                <YouTube aria-hidden="true" />
                YouTube
              </Link>
            </div>
            <div className={styles.FooterCapitalRXSectionFollowUsColumnContent}>
              <Link
                to="https://www.instagram.com/capital_rx/"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-instagram"
              >
                <Instagram aria-hidden="true" />
                Instagram
              </Link>
            </div>
            <div className={styles.FooterCapitalRXSectionFollowUsColumnContent}>
              <Link
                to="https://www.facebook.com/capitalrx"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-facebook"
              >
                <Facebook aria-hidden="true" />
                Facebook
              </Link>
            </div>
          </div>
        </Grid.Spread>
        <Grid.Spread columns={columns21212}>
          <div className={styles.FooterCapitalRXSectionAboutUs}>
            <div className={styles.FooterCapitalRXSectionAboutUsColumnTitle}>About Us</div>
            <div className={styles.FooterCapitalRXSectionAboutUsColumnContent}>
              <Link
                to="https://www.judi.health/about/our-story"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-capitalrx-overview"
              >
                Our Story
              </Link>
            </div>
            <div className={styles.FooterCapitalRXSectionAboutUsColumnContent}>
              <Link
                to="https://www.judi.health/about/leadership"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-capitalrx-leadership"
              >
                Leadership Team
              </Link>
            </div>
            <div className={styles.FooterCapitalRXSectionAboutUsColumnContent}>
              <Link
                to="https://www.judi.health/about/careers"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-capitalrx-jobs"
              >
                We&apos;re Hiring
              </Link>
            </div>
            <div className={styles.FooterCapitalRXSectionAboutUsColumnContent}>
              <Link
                to="https://www.judi.health/insights"
                className={styles.LinkWithoutUnderscore}
                testId="link-to-capitalrx-news"
              >
                In The News
              </Link>
            </div>
          </div>
        </Grid.Spread>
        <Grid.Spread columns={columns51212} data-testid="footer-contact-us-section">
          <div className={styles.FooterContactUsSection}>
            <div className={styles.FooterContactUsSectionAdvantageProgram}>
              Interested in rolling out your own {cardName} program?
            </div>
            <div className={styles.FooterContactUsSectionContactUsText}>
              Contact us to scale or white label your own card.
            </div>
            <div className={styles.FooterContactUsSectionContactUsButton}>
              <Button onClick={contactUsPressed} testId="footer-contact-us-button">
                Contact Us
              </Button>
            </div>
            <div className={styles.FooterContactUsSectionDownloadCRxAdvantageAppText}>
              Download the {cardBrandName} App
            </div>
            <DownloadAppLogo />
          </div>
        </Grid.Spread>
      </Grid>

      <Divider className={styles.SectionDividerLine} aria-hidden="true" />

      <div className={styles.CopyrightsSection}>
        <Shelf className={styles.CopyrightsSectionInfo} data-testid="capitalrx-copyright">
          &copy; {brandName}, Inc.
          <Shelf.Spacer />
          <Link
            to="/privacy-policy"
            className={styles.LinkWithoutUnderscore}
            testId="link-to-privacy-policy"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms-and-conditions"
            className={styles.LinkWithoutUnderscore}
            testId="link-to-terms"
          >
            Terms and Conditions
          </Link>
        </Shelf>
      </div>
    </footer>
  );
};

export default Footer;
